import React, { useState, useEffect, useCallback } from 'react';
import MorningRescueModal from './MorningRescueModal';
import axios from 'axios';
import { Moon, Check, X, Plus, Minus, ShoppingBag, Sparkles, AlertCircle, Settings, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_SV_SHORT = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const ACTIVITY_TYPES = [
  { value: 'gympa',      label: 'Gympa',       emoji: '🤸' },
  { value: 'simskola',   label: 'Simskola',    emoji: '🏊' },
  { value: 'idrott',     label: 'Idrott',      emoji: '⚽' },
  { value: 'fotboll',    label: 'Fotboll',     emoji: '⚽' },
  { value: 'ridning',    label: 'Ridning',     emoji: '🐴' },
  { value: 'musikskola', label: 'Musikskola',  emoji: '🎵' },
  { value: 'utflykt',    label: 'Utflykt',     emoji: '🎒' },
];
const STOCK_KEYS = [
  { key: 'trosor',   label: 'Trosor/kalsonger', emoji: '🩲' },
  { key: 'strumpor', label: 'Strumpor',         emoji: '🧦' },
  { key: 'byxor',    label: 'Byxor',            emoji: '👖' },
  { key: 'tshirts',  label: 'T-shirts',         emoji: '👕' },
];
const OUTGROWN_ITEMS = [
  { key: 'byxor',    label: 'Byxor',     emoji: '👖' },
  { key: 'skor',     label: 'Skor',      emoji: '👟' },
  { key: 'jackor',   label: 'Jacka',     emoji: '🧥' },
  { key: 'overdelar',label: 'Överdelar', emoji: '👕' },
];

// =============== SETTINGS MODAL ===============
function SettingsModal({ open, onClose, config, onSaved }) {
  const [kids, setKids] = useState(config?.kids || []);
  const [delivery, setDelivery] = useState(config?.delivery || 'both');
  const [eveningHour, setEveningHour] = useState(config?.evening_hour || 19);

  useEffect(() => {
    setKids(config?.kids || []);
    setDelivery(config?.delivery || 'both');
    setEveningHour(config?.evening_hour || 19);
  }, [config]);

  if (!open) return null;

  const updateKid = (idx, patch) => {
    setKids(kids.map((k, i) => i === idx ? { ...k, ...patch } : k));
  };

  const toggleActivity = (kidIdx, day, type) => {
    const kid = kids[kidIdx];
    const acts = kid.activities || [];
    const idx = acts.findIndex(a => a.day === day && a.type === type);
    let next;
    if (idx >= 0) {
      next = acts.filter((_, i) => i !== idx);
    } else {
      next = [...acts, { day, type }];
    }
    updateKid(kidIdx, { activities: next });
  };

  const addKid = () => {
    setKids([...kids, { name: '', age: null, activities: [] }]);
  };

  const removeKid = async (kidIdx) => {
    const kid = kids[kidIdx];
    if (kid.id && !window.confirm(`Ta bort ${kid.name}?`)) return;
    if (kid.id) {
      try {
        await axios.delete(`${API}/imorgon/kids/${kid.id}`, { withCredentials: true });
      } catch { /* already removed */ }
    }
    setKids(kids.filter((_, i) => i !== kidIdx));
  };

  const save = async () => {
    try {
      const r = await axios.post(`${API}/imorgon/config`, {
        kids: kids.map(k => ({
          id: k.id,
          name: k.name,
          age: k.age ? parseInt(k.age) : null,
          activities: k.activities || [],
        })),
        delivery,
        evening_hour: parseInt(eveningHour),
      }, { withCredentials: true });
      toast.success('Sparat 💛');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error('Kunde inte spara');
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/50 flex items-center justify-center p-4" data-testid="imorgon-settings-modal">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-purple-100 to-pink-100 px-6 py-4 rounded-t-3xl flex items-center justify-between border-b border-purple-200">
          <h2 className="text-xl font-bold text-purple-800 flex items-center gap-2">
            <Settings className="w-5 h-5" /> Mina barn & aktiviteter
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/50" data-testid="imorgon-settings-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {kids.map((kid, idx) => (
            <div key={kid.id || idx} className="bg-pink-50 rounded-2xl p-4 border-2 border-pink-100" data-testid={`imorgon-kid-settings-${idx}`}>
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="text" value={kid.name} placeholder="Namn"
                  onChange={(e) => updateKid(idx, { name: e.target.value })}
                  className="flex-1 px-3 py-2 rounded-xl border border-pink-200 bg-white focus:border-pink-500 outline-none text-sm font-semibold"
                  data-testid={`imorgon-kid-name-${idx}`}
                />
                <input
                  type="number" value={kid.age || ''} placeholder="Ålder" min="0" max="20"
                  onChange={(e) => updateKid(idx, { age: e.target.value })}
                  className="w-20 px-3 py-2 rounded-xl border border-pink-200 bg-white focus:border-pink-500 outline-none text-sm"
                  data-testid={`imorgon-kid-age-${idx}`}
                />
                <button onClick={() => removeKid(idx)} className="p-2 text-rose-500 hover:bg-rose-100 rounded-full" data-testid={`imorgon-kid-remove-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs font-bold text-purple-600 mb-2 uppercase tracking-wide">Aktiviteter per veckodag</p>
              <div className="space-y-1.5">
                {ACTIVITY_TYPES.map((at) => (
                  <div key={at.value} className="flex items-center gap-1 flex-wrap">
                    <span className="w-24 text-xs font-medium text-gray-600 flex items-center gap-1">
                      <span>{at.emoji}</span> {at.label}
                    </span>
                    {DAYS_SV_SHORT.map((d, dayIdx) => {
                      const active = (kid.activities || []).some(a => a.day === dayIdx && a.type === at.value);
                      return (
                        <button
                          key={dayIdx}
                          onClick={() => toggleActivity(idx, dayIdx, at.value)}
                          data-testid={`imorgon-act-${idx}-${at.value}-${dayIdx}`}
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold transition ${
                            active ? 'bg-purple-500 text-white shadow' : 'bg-white text-gray-400 hover:bg-purple-100'
                          }`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button
            onClick={addKid}
            data-testid="imorgon-add-kid"
            className="w-full py-3 rounded-2xl border-2 border-dashed border-pink-300 text-pink-600 hover:bg-pink-50 font-semibold flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" /> Lägg till ett barn
          </button>

          {/* Delivery settings */}
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
            <p className="text-sm font-bold text-amber-800 mb-2">Hur vill du få briefingen?</p>
            <div className="flex gap-2 flex-wrap mb-3">
              {[
                { v: 'popup', l: 'Auto-popup på kvällen' },
                { v: 'button', l: 'Bara knapp i header' },
                { v: 'both', l: 'Båda (rekommenderat)' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setDelivery(opt.v)}
                  data-testid={`imorgon-delivery-${opt.v}`}
                  className={`px-3 py-2 rounded-xl text-xs font-bold ${delivery === opt.v ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 hover:bg-amber-100'}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            {(delivery === 'popup' || delivery === 'both') && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Popup-tid:</span>
                <input
                  type="number" min="0" max="23" value={eveningHour}
                  onChange={(e) => setEveningHour(e.target.value)}
                  className="w-16 px-2 py-1 rounded-lg border border-amber-300"
                  data-testid="imorgon-evening-hour"
                />
                <span className="text-gray-500 text-xs">:00 (din lokala tid)</span>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white px-6 py-3 border-t border-gray-100 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-bold">Avbryt</button>
          <button onClick={save} data-testid="imorgon-settings-save" className="px-5 py-2 rounded-xl bg-purple-500 hover:bg-purple-600 text-white text-sm font-bold shadow">
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}

// =============== STOCK COUNTER ROW ===============
function StockRow({ kid, onChange }) {
  const adjust = async (key, delta) => {
    try {
      await axios.post(`${API}/imorgon/kids/${kid.kid_id || kid.id}/stock`, {
        key, delta,
      }, { withCredentials: true });
      onChange();
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {STOCK_KEYS.map((s) => {
        const slot = (kid.stock && kid.stock[s.key]) || { count: 0, warn_at: 0 };
        const lowStock = slot.count <= slot.warn_at;
        return (
          <div
            key={s.key}
            data-testid={`imorgon-stock-${kid.kid_id || kid.id}-${s.key}`}
            className={`rounded-xl p-2 border-2 ${lowStock ? 'border-rose-300 bg-rose-50' : 'border-gray-200 bg-white'}`}
          >
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 font-semibold text-gray-700">
                <span className="text-base">{s.emoji}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </span>
              {lowStock && <AlertCircle className="w-3 h-3 text-rose-500" />}
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <button onClick={() => adjust(s.key, -1)} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center" data-testid={`imorgon-stock-dec-${kid.kid_id || kid.id}-${s.key}`}>
                <Minus className="w-3 h-3" />
              </button>
              <span className={`font-bold text-lg ${lowStock ? 'text-rose-600' : 'text-gray-700'}`}>{slot.count}</span>
              <button onClick={() => adjust(s.key, 1)} className="w-7 h-7 rounded-full bg-pink-100 hover:bg-pink-200 flex items-center justify-center" data-testid={`imorgon-stock-inc-${kid.kid_id || kid.id}-${s.key}`}>
                <Plus className="w-3 h-3" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============== MAIN BRIEFING MODAL ===============
export default function ImorgonBriefing({ open, onClose }) {
  const [briefing, setBriefing] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [morningKid, setMorningKid] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API}/imorgon/briefing`, { withCredentials: true }),
        axios.get(`${API}/imorgon/config`, { withCredentials: true }),
      ]);
      setBriefing(b.data);
      setConfig(c.data);
    } catch (e) {
      toast.error('Kunde inte ladda briefingen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleDone = async (kidId, item, done) => {
    try {
      await axios.post(`${API}/imorgon/briefing/done`, {
        kid_id: kidId, item, done,
      }, { withCredentials: true });
      // Optimistic update
      setBriefing((b) => {
        if (!b) return b;
        const next = { ...b };
        next.kids = next.kids.map((k) => {
          if (k.kid_id !== kidId) return k;
          return { ...k, items: k.items.map((it) => it.item === item ? { ...it, done } : it) };
        });
        return next;
      });
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const markOutgrown = async (kidId, item) => {
    try {
      await axios.post(`${API}/imorgon/kids/${kidId}/outgrown`, {
        item, reason: 'Växt ur — köp nya',
      }, { withCredentials: true });
      toast.success('Lagt till på inköpslistan 🛍️');
      load();
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const dismissShopping = async (kidId, item) => {
    try {
      await axios.post(`${API}/imorgon/kids/${kidId}/shopping/${encodeURIComponent(item)}/dismiss`, {}, { withCredentials: true });
      load();
    } catch { /* dismiss already applied */ }
  };

  const dismissPopup = async () => {
    try {
      await axios.post(`${API}/imorgon/briefing/dismiss`, {}, { withCredentials: true });
    } catch { /* dismiss already applied */ }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[280] bg-gradient-to-br from-indigo-900/70 via-purple-900/60 to-pink-900/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="imorgon-briefing-modal">
      <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-3xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border border-purple-200">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 px-5 sm:px-6 py-4 rounded-t-3xl flex items-center justify-between border-b border-purple-200">
          <div>
            <div className="flex items-center gap-2 text-purple-800">
              <Moon className="w-5 h-5" />
              <h1 className="text-lg sm:text-xl font-bold">Imorgon-briefing</h1>
            </div>
            {briefing && (
              <p className="text-xs text-purple-600 mt-0.5">
                {briefing.weekday_sv} {briefing.date} · ditt mind kan vila ikväll 💛
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSettingsOpen(true)}
              className="p-2 rounded-full hover:bg-white/60 text-purple-700"
              title="Inställningar"
              data-testid="imorgon-open-settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={dismissPopup}
              className="p-2 rounded-full hover:bg-white/60 text-purple-700"
              data-testid="imorgon-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="p-12 text-center text-purple-500">Laddar…</div>
        )}

        {!loading && briefing && (
          <div className="p-4 sm:p-6 space-y-5">
            {/* Evening tasks — laundry alerts */}
            {briefing.evening_tasks && briefing.evening_tasks.length > 0 && (
              <div className="bg-gradient-to-r from-rose-100 to-amber-100 rounded-2xl p-4 border-2 border-rose-300 shadow-sm" data-testid="imorgon-evening-tasks">
                <h3 className="text-sm font-bold text-rose-800 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Gör det NU ikväll — annars är det kris imorgon
                </h3>
                <div className="space-y-1.5">
                  {briefing.evening_tasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 bg-white/70 rounded-xl px-3 py-2 text-sm">
                      <span className="text-lg">{t.emoji}</span>
                      <span className="flex-1 text-rose-900 font-medium">{t.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shopping list */}
            {briefing.shopping && briefing.shopping.length > 0 && (
              <div className="bg-gradient-to-r from-amber-100 to-yellow-50 rounded-2xl p-4 border border-amber-200" data-testid="imorgon-shopping-list">
                <h3 className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4" /> Behöver köpas
                </h3>
                <div className="space-y-1.5">
                  {briefing.shopping.map((s, i) => (
                    <div key={`${s.kid_id}-${s.item}-${i}`} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 text-sm">
                      <span className="font-bold text-amber-900">{s.kid_name}</span>
                      <span className="text-gray-500">·</span>
                      <span className="flex-1 text-gray-700">{s.item}</span>
                      <span className="text-[10px] text-gray-400 italic hidden sm:inline">{s.reason}</span>
                      <button
                        onClick={() => dismissShopping(s.kid_id, s.item)}
                        className="text-xs text-gray-400 hover:text-rose-500 p-1"
                        title="Bocka av (köpt)"
                        data-testid={`imorgon-shop-dismiss-${s.kid_id}-${s.item}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-kid sections */}
            {briefing.kids.map((kid) => (
              <div key={kid.kid_id} className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100" data-testid={`imorgon-kid-${kid.kid_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-bold text-purple-800">
                    🧒 {kid.name}
                    {kid.age != null && <span className="text-xs text-gray-400 font-normal ml-1">· {kid.age} år</span>}
                  </h3>
                  <div className="flex gap-1.5 items-center flex-wrap justify-end">
                    {kid.activities_today.length > 0 && kid.activities_today.map((a, i) => (
                      <span key={i} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                        {a.label}
                      </span>
                    ))}
                    <button
                      onClick={() => setMorningKid({ id: kid.kid_id, name: kid.name })}
                      data-testid={`morning-trigger-${kid.kid_id}`}
                      title="Morgon-läge"
                      className="text-xs px-2 py-0.5 rounded-full bg-amber-200 hover:bg-amber-300 text-amber-900 font-bold flex items-center gap-1"
                    >
                      ☀️ Morgon-läge
                    </button>
                  </div>
                </div>

                {/* Items checklist */}
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Lägg fram ikväll</p>
                <div className="space-y-1 mb-3">
                  {kid.items.map((it) => (
                    <label
                      key={it.item}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl cursor-pointer transition ${it.done ? 'bg-green-50 line-through opacity-60' : 'bg-gray-50 hover:bg-purple-50'}`}
                      data-testid={`imorgon-item-${kid.kid_id}-${it.item}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!it.done}
                        onChange={(e) => toggleDone(kid.kid_id, it.item, e.target.checked)}
                        className="w-4 h-4 accent-purple-500"
                      />
                      <span className="flex-1 text-sm text-gray-700">{it.item}</span>
                      {it.category === 'aktivitet' && (
                        <span className="text-[10px] text-purple-500 font-semibold">{it.activity_label}</span>
                      )}
                    </label>
                  ))}
                </div>

                {/* Stock counters */}
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1.5 mt-3">Klädförråd · varna när litet</p>
                <StockRow kid={kid} onChange={load} />

                {/* Outgrown buttons */}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {OUTGROWN_ITEMS.map((o) => {
                    const already = (kid.shopping || []).some(s => s.item === o.key);
                    return (
                      <button
                        key={o.key}
                        onClick={() => markOutgrown(kid.kid_id, o.key)}
                        disabled={already}
                        data-testid={`imorgon-outgrown-${kid.kid_id}-${o.key}`}
                        className={`text-[11px] px-2 py-1 rounded-full font-semibold transition ${
                          already
                            ? 'bg-amber-100 text-amber-600 cursor-not-allowed'
                            : 'bg-white border border-amber-200 text-amber-700 hover:bg-amber-50'
                        }`}
                      >
                        {already ? '✓' : '+'} {o.emoji} {o.label} {already && 'tillagd'}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Footer note */}
            <div className="text-center text-xs text-purple-500 italic px-4 py-3">
              <Sparkles className="w-4 h-4 inline mr-1" />
              När du har gjort allt här — släpp tanken. Hjärnan vilar nu. Allt blir bra imorgon 💛
            </div>
          </div>
        )}

        {/* Empty state if no kids */}
        {!loading && briefing && briefing.kids.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-600 mb-3">Lägg till barnen så börjar jag bygga briefingar 💛</p>
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-4 py-2 rounded-xl bg-purple-500 text-white font-bold"
              data-testid="imorgon-empty-add-kids"
            >
              Lägg till barn
            </button>
          </div>
        )}
      </div>

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        config={config}
        onSaved={() => load()}
      />

      {morningKid && (
        <MorningRescueModal
          kidId={morningKid.id}
          kidName={morningKid.name}
          onClose={() => setMorningKid(null)}
        />
      )}
    </div>
  );
}
